import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { fetchSiteContent } from "../lib/api";
import { FALLBACK_CONTENT, type SiteContent } from "../lib/fallback-content";

interface SiteContentContextValue {
  content:  SiteContent;
  loading:  boolean;
  fromWP:   boolean; // true = loaded from WordPress, false = using fallback
}

const SiteContentContext = createContext<SiteContentContextValue>({
  content: FALLBACK_CONTENT,
  loading: true,
  fromWP:  false,
});

export function SiteContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<SiteContent>(FALLBACK_CONTENT);
  const [loading, setLoading] = useState(true);
  const [fromWP,  setFromWP]  = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSiteContent()
      .then((data) => {
        if (cancelled) return;
        setContent(data);
        // If data differs from fallback, WP responded
        setFromWP(data !== FALLBACK_CONTENT);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <SiteContentContext.Provider value={{ content, loading, fromWP }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}
